import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { Input, Label, TextArea } from "@/components/ui/Input";
import {
  formatMiscTimeCardActionLabel,
  formatMiscTimeCardInstant,
  miscTimeCardDateTimeLocalToIso,
  miscTimeCardIsoToDateTimeLocal,
  type MiscTimeCardRow,
  type TimeCardChangeAction,
} from "@/lib/miscQuickBooksTimeCardsApi";

export type PersonalTimeCardChangeRequestModalMode = "edit" | "delete" | "add";

export function resolvePersonalTimeCardChangeRequestModal(input: {
  mode: PersonalTimeCardChangeRequestModalMode;
  row: MiscTimeCardRow | null;
  calendarDate: string;
}): {
  action: TimeCardChangeAction;
  title: string;
  calendarDate: string;
  quickbooksTimeTimesheetId: number | null;
  laborMiscTimeEntryId: number | null;
  startLocal: string;
  endLocal: string;
  description: string;
} {
  const row = input.row;
  if (input.mode === "add") {
    return {
      action: "add_qbt_timesheet",
      title: "Request missing time",
      calendarDate: input.calendarDate,
      quickbooksTimeTimesheetId: null,
      laborMiscTimeEntryId: null,
      startLocal: "",
      endLocal: "",
      description: "",
    };
  }
  if (row == null) {
    throw new Error("Row is required.");
  }
  if (input.mode === "delete") {
    if (row.source === "qbt") {
      return {
        action: "delete_qbt_timesheet",
        title: "Request delete",
        calendarDate: row.calendarDate,
        quickbooksTimeTimesheetId: row.quickbooksTimeTimesheetId,
        laborMiscTimeEntryId: null,
        startLocal: miscTimeCardIsoToDateTimeLocal(row.start),
        endLocal: miscTimeCardIsoToDateTimeLocal(row.end),
        description: "",
      };
    }
    return {
      action: "delete_misc_entry",
      title: "Request delete",
      calendarDate: row.calendarDate,
      quickbooksTimeTimesheetId: null,
      laborMiscTimeEntryId: row.laborMiscTimeEntryId,
      startLocal: miscTimeCardIsoToDateTimeLocal(row.start),
      endLocal: miscTimeCardIsoToDateTimeLocal(row.end),
      description: row.label,
    };
  }
  if (row.source === "qbt") {
    return {
      action: "edit_qbt_timesheet",
      title: "Request time change",
      calendarDate: row.calendarDate,
      quickbooksTimeTimesheetId: row.quickbooksTimeTimesheetId,
      laborMiscTimeEntryId: null,
      startLocal: miscTimeCardIsoToDateTimeLocal(row.start),
      endLocal: miscTimeCardIsoToDateTimeLocal(row.end),
      description: "",
    };
  }
  return {
    action: "edit_misc_entry",
    title: "Request time change",
    calendarDate: row.calendarDate,
    quickbooksTimeTimesheetId: null,
    laborMiscTimeEntryId: row.laborMiscTimeEntryId,
    startLocal: miscTimeCardIsoToDateTimeLocal(row.start),
    endLocal: miscTimeCardIsoToDateTimeLocal(row.end),
    description: row.label,
  };
}

export function PersonalTimeCardChangeRequestModal({
  open,
  mode,
  row,
  calendarDate,
  submitting,
  submitError,
  onCancel,
  onSubmit,
}: {
  open: boolean;
  mode: PersonalTimeCardChangeRequestModalMode;
  row: MiscTimeCardRow | null;
  calendarDate: string;
  submitting: boolean;
  submitError: string | null;
  onCancel: () => void;
  onSubmit: (input: {
    action: TimeCardChangeAction;
    calendarDate: string;
    quickbooksTimeTimesheetId?: number | null;
    laborMiscTimeEntryId?: number | null;
    proposedPayload: Record<string, unknown>;
    reason: string;
  }) => void;
}): JSX.Element | null {
  const [reason, setReason] = useState("");
  const [startLocal, setStartLocal] = useState("");
  const [endLocal, setEndLocal] = useState("");
  const [description, setDescription] = useState("");

  const resolved = open
    ? resolvePersonalTimeCardChangeRequestModal({ mode, row, calendarDate })
    : null;

  useEffect(() => {
    if (!open) {
      return;
    }
    const next = resolvePersonalTimeCardChangeRequestModal({ mode, row, calendarDate });
    setReason("");
    setStartLocal(next.startLocal);
    setEndLocal(next.endLocal);
    setDescription(next.description);
  }, [open, mode, row?.rowKey, calendarDate]);

  if (!open || resolved == null) {
    return null;
  }

  const showTimeFields = mode === "edit" || mode === "add";
  const showDescription = resolved.action === "edit_misc_entry";

  return createPortal(
    <div {...hubModalOverlayProps(true)}>
      <Card className="mt-6 w-full max-w-lg shadow-xl">
        <CardHeader className="border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">{resolved.title}</h2>
          <p className="mt-1 text-sm text-slate-600">
            {formatMiscTimeCardActionLabel(resolved.action)} for {resolved.calendarDate}. An admin
            must approve before QuickBooks Time or hub records change.
          </p>
        </CardHeader>
        <CardBody className="space-y-4">
          {mode === "delete" && row != null ? (
            <dl className="grid gap-2 text-sm">
              <div>
                <dt className="text-muted-foreground">Entry</dt>
                <dd>{row.label}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Start</dt>
                <dd>{formatMiscTimeCardInstant(row.start)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">End</dt>
                <dd>{formatMiscTimeCardInstant(row.end)}</dd>
              </div>
            </dl>
          ) : null}

          {showTimeFields ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="misc-time-card-start">Start</Label>
                <Input
                  id="misc-time-card-start"
                  type="datetime-local"
                  className="mt-1.5"
                  value={startLocal}
                  onChange={(event) => setStartLocal(event.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="misc-time-card-end">End</Label>
                <Input
                  id="misc-time-card-end"
                  type="datetime-local"
                  className="mt-1.5"
                  value={endLocal}
                  onChange={(event) => setEndLocal(event.target.value)}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Leave blank if this punch is still open on the clock.
                </p>
              </div>
            </div>
          ) : null}

          {showDescription ? (
            <div>
              <Label htmlFor="misc-time-card-description">Description</Label>
              <Input
                id="misc-time-card-description"
                className="mt-1.5"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
          ) : null}

          <div>
            <Label htmlFor="misc-time-card-reason">Reason for change</Label>
            <TextArea
              id="misc-time-card-reason"
              className="mt-1.5 min-h-[88px]"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Explain what should change and why."
            />
          </div>

          {submitError != null ? (
            <p className="text-sm text-destructive">{submitError}</p>
          ) : null}

          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" disabled={submitting} onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={submitting || reason.trim() === ""}
              onClick={() => {
                const proposedPayload: Record<string, unknown> = {};
                if (showTimeFields) {
                  proposedPayload.start = miscTimeCardDateTimeLocalToIso(startLocal);
                  if (endLocal.trim() !== "") {
                    proposedPayload.end = miscTimeCardDateTimeLocalToIso(endLocal);
                  }
                }
                if (showDescription) {
                  proposedPayload.description = description.trim();
                }
                onSubmit({
                  action: resolved.action,
                  calendarDate: resolved.calendarDate,
                  quickbooksTimeTimesheetId: resolved.quickbooksTimeTimesheetId,
                  laborMiscTimeEntryId: resolved.laborMiscTimeEntryId,
                  proposedPayload,
                  reason: reason.trim(),
                });
              }}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
              Submit request
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>,
    document.body,
  );
}
